import { CaseResult, IProductOffering } from "config";
import { AppError } from "config/errors";
import {
  acceptProductOfferingMutation,
  IUpdateProductOfferingInput,
  updateProductOfferingMutation,
} from "gql/mutations/product-offering";
import { IAdjustCoverageInput } from "gql/queries/estimate";
import { adjustProductOfferingQuery, fetchProductOfferings } from "gql/queries/productOffering";
import { getActiveAccount } from "utils/account";

import { createAsyncThunk } from "@reduxjs/toolkit";

export const getProductOfferings = createAsyncThunk(
  "offerings/getProductOfferings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchProductOfferings();
      const activeAccount = getActiveAccount(response.data.me);
      const latestInsuranceApplication = activeAccount?.latestInsuranceApplication;
      const accountProductOfferings = activeAccount?.productsOfferings || [];
      const productOfferings: IProductOffering[] = accountProductOfferings.map(
        (offering: IProductOffering) => {
          return {
            ...offering,
            productSlug: offering.product.slug,
            allocationsCoveredPercentage: offering.allocationCoverage,
            annualPremium:
              offering.premiums.find(premium => premium.billingFrequency === "annually")?.amount ||
              0,
            monthlyPremium:
              offering.premiums.find(premium => premium.billingFrequency === "monthly")?.amount ||
              0,
          };
        }
      );

      return { productOfferings, latestInsuranceApplication };
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

export const offeringsReviewPolling = createAsyncThunk(
  "offerings/offeringsReviewPolling",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await dispatch(getProductOfferings()).unwrap();

      const caseResult: CaseResult = response.latestInsuranceApplication?.result || "";
      const caseId = response.latestInsuranceApplication?.id || "";

      return { caseResult, caseId, productOfferings: response.productOfferings };
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

export const acceptProductOffering = createAsyncThunk(
  "offerings/acceptProductOffering",
  async (offeringId: string, { rejectWithValue }) => {
    try {
      const { data } = await acceptProductOfferingMutation({ offeringId });

      return { success: data.acceptProductOffering.response.success };
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

export const adjustProductOffering = createAsyncThunk(
  "offerings/adjustProductOffering",
  async (input: IAdjustCoverageInput, { rejectWithValue }) => {
    try {
      const response = await adjustProductOfferingQuery(input);

      return {
        ...response.data.adjustProductOffering,
        offeringId: input.id,
      };
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

export const updateProductOffering = createAsyncThunk(
  "offerings/updateProductOffering",
  async (input: IUpdateProductOfferingInput, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await updateProductOfferingMutation(input);
      await dispatch(getProductOfferings()).unwrap();

      return { success: data.updateProductOffering.response.success };
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);
